import moment from 'moment'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import {
  InsightTableGroupBy,
  InsightTableType,
} from 'modules/finance/components/FinancialWorkspace/types/table'

import { useDataCollectionSource } from './hooks/useDataCollectionSource'
import { useDataCollectionColumns } from './hooks/useDataCollectionColumns'
import { useHeaders } from './hooks/useHeaders'
import { referenceRowType } from './hooks/expanderHelpers'
import { useComparisonHeaders } from './hooks/useComparisonHeaders'

interface DataCollectionTableProps {
  title: string
  search?: string
  type?: InsightTableType
  groupBy: InsightTableGroupBy
  defaultGroupBy: InsightTableGroupBy
  useOnClickHeader?: () => (date: moment.Moment) => void
}

/**
 * OneDataCollection-based table for Financial Workspace
 *
 * Renders financial data using OneDataCollection with support for:
 * - Dynamic columns based on date range and period selection
 * - Nested/collapsible rows for grouped data
 * - Comparison mode with growth indicators
 * - Infinite scroll pagination
 */
export const DataCollectionTable = ({ title, type, useOnClickHeader, groupBy, defaultGroupBy, search }: DataCollectionTableProps) => {
  const onClickHeader = useOnClickHeader?.()

  const { hasComparisonDate, insightTableType, formattedState } = useFinancialWorkspace()

  const effectiveType = insightTableType ?? type

  const dataCollectionKey = useUnsafeMemo(() => JSON.stringify({ formattedState, groupBy }), [formattedState, groupBy])

  const headerData = useHeaders(onClickHeader)
  const comparisonHeaders = useComparisonHeaders()

  const headerColumnLabels = useUnsafeMemo(() => {
    const obj: { [key: string]: string | undefined } = {}

    headerData.forEach(({ label, chartLabel }) => {
      if (chartLabel) obj[chartLabel] = label
    })

    return obj
  }, [headerData])

  const headers = useUnsafeMemo(() => {
    return headerData.map(({ label, chartLabel }) => chartLabel ?? label)
  }, [headerData])

  const isGrouped = groupBy !== defaultGroupBy

  const source = useDataCollectionSource({
    search,
    groupBy,
    headers,
    isGrouped,
    type: effectiveType
  })

  const headerGroupLabels = useUnsafeMemo(() => {
    if (comparisonHeaders) {
      const obj: Record<string, string> = {}

      comparisonHeaders.forEach(({ label }) => {
        obj[label] = label
      })

      return obj
    }
  }, [comparisonHeaders])

  const columns = useDataCollectionColumns({
    title,
    headers,
    groupBy,
    headerColumnLabels,
    showComparison: hasComparisonDate,
    comparisonHeaders: comparisonHeaders?.map(({ label }) => label)
  })

  const visualizations = useUnsafeMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns,
          referenceRowType,
          headerGroupLabels,
          frozenColumns: 1 as const,
        },
      },
    ],
    [columns]
  )

  return <OneDataCollection key={dataCollectionKey} source={source} visualizations={visualizations} />
}
